/**
 * Bankumsätze: einlesen und zuordnen.
 *
 * Der Ablauf ist immer derselbe: Auszug hochladen, Vorschläge durchsehen,
 * zuordnen. Was privat oder unerheblich ist, wird gekennzeichnet und
 * verschwindet aus der Arbeitsliste — sonst bleibt sie voll und wird nicht
 * mehr angesehen.
 *
 * Dubletten fängt die Datenbank ab (Unique-Index über Konto und Prüfsumme).
 * Deshalb ist ein zweiter Import derselben Datei harmlos: Er meldet
 * Duplikate, statt Beträge zu verdoppeln.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Dialog } from '../bausteine/Dialog';
import { Betragsfeld, Textbereich, Textfeld } from '../bausteine/Feld';
import { Seite } from '../bausteine/Layout';
import { Marker } from '../bausteine/Marker';
import { Fehlermeldung, Meldung } from '../bausteine/Meldung';
import { Laedt, Leer } from '../bausteine/Zustand';
import { useAbruf, useVorgang } from '../lib/abruf';
import { alsAbfrage, api } from '../lib/api';
import { datum, datumZeit, heute } from '../lib/format';
import { alsCent, euro, formatiereCent } from '../lib/geld';
import { useSitzung } from '../sitzung/SitzungKontext';
import { Zugangskarte } from './BankZugang';
import type {
  Bankkonto,
  Bankumsatz,
  ImportErgebnis,
  UmsatzListe,
  Zuordnungsvorschlag,
} from '../lib/typen';

export function Bank(): ReactNode {
  const [filter, setzeFilter] = useSearchParams();
  const { darfSchreiben } = useSitzung();
  const lese = (name: string): string => filter.get(name) ?? '';

  const konten = useAbruf<Bankkonto[]>('/api/bankkonten');
  const vorgang = useVorgang();

  const [kontoDialog, setzeKontoDialog] = useState(false);
  const [umsatzDialog, setzeUmsatzDialog] = useState(false);
  const [importBericht, setzeImportBericht] = useState<ImportErgebnis | null>(null);
  const [zuordnen, setzeZuordnen] = useState<Bankumsatz | null>(null);

  function aendere(name: string, wert: string): void {
    const neu = new URLSearchParams(filter);
    if (wert === '') neu.delete(name);
    else neu.set(name, wert);
    setzeFilter(neu, { replace: true });
  }

  const pfad = useMemo(
    () =>
      `/api/bankumsaetze${alsAbfrage({
        bankkontoId: lese('konto'),
        status: lese('status'),
        offen: lese('offen'),
        von: lese('von'),
        bis: lese('bis'),
        proSeite: 100,
      })}`,
    [filter.toString()],
  );

  const umsaetze = useAbruf<UmsatzListe>(pfad);

  async function importiere(kontoId: string, datei: File): Promise<void> {
    setzeImportBericht(null);
    const ergebnis = await vorgang.fuehreAus(() =>
      api.sendeDatei<ImportErgebnis>(`/api/bankkonten/${kontoId}/import`, datei),
    );
    if (ergebnis !== undefined) {
      setzeImportBericht(ergebnis);
      umsaetze.neuLaden();
      konten.neuLaden();
    }
  }

  return (
    <Seite
      titel="Bank"
      untertitel={umsaetze.daten ? `${umsaetze.daten.gesamt} Umsätze` : undefined}
      aktionen={
        darfSchreiben ? (
          <>
            {(konten.daten ?? []).length > 0 ? (
              <button type="button" className="knopf" onClick={() => setzeUmsatzDialog(true)}>
                Umsatz erfassen
              </button>
            ) : null}
            <button type="button" className="knopf" onClick={() => setzeKontoDialog(true)}>
              Konto anlegen
            </button>
          </>
        ) : null
      }
    >
      <div className="stapel">
        <Fehlermeldung text={vorgang.fehler} />

        {importBericht ? (
          <Meldung
            ton={importBericht.neu > 0 ? 'gut' : 'info'}
            titel={`${importBericht.gelesen} Umsätze gelesen (${importBericht.format === 'camt053' ? 'CAMT.053' : 'CSV'}).`}
          >
            {importBericht.neu} neu übernommen, {importBericht.duplikate} bereits
            vorhanden.
            {importBericht.hinweise?.length ? (
              <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                {importBericht.hinweise.map((h) => (
                  <li key={h} className="klein">
                    {h}
                  </li>
                ))}
              </ul>
            ) : null}
          </Meldung>
        ) : null}

        <Konten
          konten={konten}
          darfSchreiben={darfSchreiben}
          importiere={importiere}
          laeuft={vorgang.laeuft}
          nachAbruf={() => {
            umsaetze.neuLaden();
            konten.neuLaden();
          }}
        />

        <div className="karte">
          <div className="filterleiste">
            <div className="feld">
              <label htmlFor="konto">Konto</label>
              <select id="konto" value={lese('konto')} onChange={(e) => aendere('konto', e.target.value)}>
                <option value="">alle</option>
                {(konten.daten ?? []).map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.bezeichnung}
                  </option>
                ))}
              </select>
            </div>
            <div className="feld">
              <label htmlFor="status">Status</label>
              <select id="status" value={lese('status')} onChange={(e) => aendere('status', e.target.value)}>
                <option value="">alle</option>
                <option value="offen">Offen</option>
                <option value="zugeordnet">Zugeordnet</option>
                <option value="privat">Privat</option>
                <option value="ignoriert">Unerheblich</option>
              </select>
            </div>
            <div className="feld">
              <label htmlFor="von">Von</label>
              <input id="von" type="date" value={lese('von')} onChange={(e) => aendere('von', e.target.value)} />
            </div>
            <div className="feld">
              <label htmlFor="bis">Bis</label>
              <input id="bis" type="date" value={lese('bis')} onChange={(e) => aendere('bis', e.target.value)} />
            </div>
            <div className="feld">
              <label htmlFor="offen">Arbeitsliste</label>
              <select id="offen" value={lese('offen')} onChange={(e) => aendere('offen', e.target.value)}>
                <option value="">alle</option>
                <option value="true">nur unerledigte</option>
              </select>
            </div>
          </div>

          {umsaetze.fehler ? (
            <div className="karte-koerper">
              <Fehlermeldung text={umsaetze.fehler} />
            </div>
          ) : !umsaetze.daten ? (
            <Laedt was="Umsätze werden geladen" />
          ) : umsaetze.daten.umsaetze.length === 0 ? (
            <Leer
              titel="Keine Umsätze"
              hinweis="Kontoauszug als CAMT.053-XML oder CSV hochladen — das Format wird am Inhalt erkannt."
            />
          ) : (
            <div className="tabelle-rahmen">
              <table className="tabelle">
                <thead>
                  <tr>
                    <th className="schmal">Datum</th>
                    <th>Gegenpartei und Zweck</th>
                    <th className="schmal">Konto</th>
                    <th className="zahl">Betrag</th>
                    <th className="schmal">Status</th>
                    {darfSchreiben ? <th className="schmal" /> : null}
                  </tr>
                </thead>
                <tbody>
                  {umsaetze.daten.umsaetze.map((u) => {
                    const betragCent = alsCent(u.betrag);
                    return (
                      <tr key={u.id}>
                        <td className="schmal">{datum(u.buchungsdatum)}</td>
                        <td>
                          <span className="haupttext">{u.gegenparteiName ?? 'ohne Angabe'}</span>
                          {u.verwendungszweck ? (
                            <div className="nebentext">{u.verwendungszweck}</div>
                          ) : null}
                          {u.buchungstext ? (
                            <div className="nebentext still">{u.buchungstext}</div>
                          ) : null}
                        </td>
                        <td className="schmal klein still">{u.kontoBezeichnung}</td>
                        <td className="zahl">
                          {/* Das Vorzeichen kommt aus dem Betrag, wie ihn
                              der Import gespeichert hat — bei CAMT aus
                              CdtDbtInd, nicht aus dem Betragsfeld. */}
                          <span style={betragCent < 0 ? undefined : { color: 'var(--gut)' }}>
                            {formatiereCent(betragCent, false)}
                          </span>
                          {u.status === 'teilzugeordnet' ? (
                            <div className="nebentext">{euro(u.zugeordnet, false)} zugeordnet</div>
                          ) : null}
                        </td>
                        <td className="schmal">
                          <Marker status={u.status} />
                        </td>
                        {darfSchreiben ? (
                          <td className="schmal">
                            {/* Auch ein zugeordneter Umsatz bleibt
                                anklickbar: Eine falsche Zuordnung muss sich
                                lösen lassen, ohne in die Datenbank zu
                                greifen. */}
                            <button
                              type="button"
                              className="knopf klein"
                              onClick={() => setzeZuordnen(u)}
                            >
                              {u.status === 'offen' ? 'Zuordnen' : 'Bearbeiten'}
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {kontoDialog ? (
        <KontoDialog
          schliessen={() => setzeKontoDialog(false)}
          fertig={() => {
            setzeKontoDialog(false);
            konten.neuLaden();
          }}
        />
      ) : null}

      {umsatzDialog ? (
        <UmsatzDialog
          konten={konten.daten ?? []}
          schliessen={() => setzeUmsatzDialog(false)}
          fertig={() => {
            setzeUmsatzDialog(false);
            umsaetze.neuLaden();
          }}
        />
      ) : null}

      {zuordnen ? (
        <ZuordnenDialog
          umsatz={zuordnen}
          schliessen={() => setzeZuordnen(null)}
          fertig={() => {
            setzeZuordnen(null);
            umsaetze.neuLaden();
          }}
        />
      ) : null}
    </Seite>
  );
}

// =========================================================================
//  Konten
// =========================================================================

function Konten({
  konten,
  darfSchreiben,
  importiere,
  laeuft,
  nachAbruf,
}: {
  konten: ReturnType<typeof useAbruf<Bankkonto[]>>;
  darfSchreiben: boolean;
  importiere: (kontoId: string, datei: File) => Promise<void>;
  laeuft: boolean;
  nachAbruf: () => void;
}): ReactNode {
  if (!konten.daten) return null;
  if (konten.daten.length === 0) {
    return (
      <Meldung ton="info" titel="Noch kein Bankkonto.">
        Für den Import eines Kontoauszugs braucht es ein Konto — Girokonto,
        Kreditkarte, PayPal oder Bargeld.
      </Meldung>
    );
  }

  return (
    <div className="raster drei">
      {konten.daten.map((k) => (
        <div className="karte" key={k.id}>
          <div className="karte-koerper">
            <div className="reihe verteilt">
              <strong>{k.bezeichnung}</strong>
              <span className="klein still">{k.art}</span>
            </div>
            {k.iban ? <div className="klein fest still">{k.iban}</div> : null}
            <div className="klein still" style={{ marginTop: '0.5rem' }}>
              {k.letzterImportAm
                ? `Letzter Import: ${datumZeit(k.letzterImportAm)}`
                : 'Noch nichts eingelesen'}
            </div>
            {darfSchreiben ? (
              <label className="klein" style={{ display: 'block', marginTop: '0.625rem' }}>
                <span className="leise">Auszug einlesen (CAMT.053 oder CSV)</span>
                <input
                  type="file"
                  accept=".xml,.csv,.txt,text/csv,application/xml,text/xml"
                  disabled={laeuft}
                  style={{ marginTop: '0.25rem' }}
                  onChange={(e) => {
                    const datei = e.target.files?.[0];
                    if (datei) void importiere(k.id, datei);
                    e.target.value = '';
                  }}
                />
              </label>
            ) : null}

            {/* Der Abruf über die Schnittstelle steht bewusst unter dem
                Datei-Import und nicht an seiner Stelle: Beide bleiben
                nebeneinander benutzbar, und beide laufen durch dieselbe
                Dublettenprüfung. */}
            <Zugangskarte konto={k} darfSchreiben={darfSchreiben} nachAbruf={nachAbruf} />
          </div>
        </div>
      ))}
    </div>
  );
}

function KontoDialog({
  schliessen,
  fertig,
}: {
  schliessen: () => void;
  fertig: () => void;
}): ReactNode {
  const vorgang = useVorgang();
  const [bezeichnung, setzeBezeichnung] = useState('');
  const [art, setzeArt] = useState('giro');
  const [iban, setzeIban] = useState('');
  const [bic, setzeBic] = useState('');
  const [bankName, setzeBankName] = useState('');
  const [saldo, setzeSaldo] = useState('');
  const [saldoAm, setzeSaldoAm] = useState('');

  async function speichern(): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden('/api/bankkonten', {
        bezeichnung: bezeichnung.trim(),
        art,
        ...(iban.trim() ? { iban: iban.trim() } : {}),
        ...(bic.trim() ? { bic: bic.trim() } : {}),
        ...(bankName.trim() ? { bankName: bankName.trim() } : {}),
        ...(saldo.trim() ? { anfangssaldo: saldo } : {}),
        ...(saldoAm ? { anfangssaldoAm: saldoAm } : {}),
      }),
    );
    if (ergebnis !== undefined) fertig();
  }

  return (
    <Dialog
      titel="Bankkonto anlegen"
      schliessen={schliessen}
      fuss={
        <>
          <button type="button" className="knopf" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="button"
            className="knopf haupt"
            disabled={bezeichnung.trim() === '' || vorgang.laeuft}
            onClick={() => void speichern()}
          >
            Anlegen
          </button>
        </>
      }
    >
      <div className="stapel">
        <Fehlermeldung text={vorgang.fehler} />
        <div className="formularraster">
          <Textfeld
            beschriftung="Bezeichnung"
            wert={bezeichnung}
            setzeWert={setzeBezeichnung}
            pflicht
            breit
            platzhalter="Geschäftskonto Sparkasse"
          />
          <div className="feld">
            <label htmlFor="art">Art</label>
            <select id="art" value={art} onChange={(e) => setzeArt(e.target.value)}>
              <option value="giro">Girokonto</option>
              <option value="kreditkarte">Kreditkarte</option>
              <option value="paypal">PayPal</option>
              <option value="bar">Bargeld</option>
              <option value="sonstiges">Sonstiges</option>
            </select>
          </div>
          <Textfeld
            beschriftung="IBAN"
            wert={iban}
            setzeWert={setzeIban}
            hinweis="Wird geprüft; beim Import muss der Auszug dazu passen"
          />
          <Textfeld beschriftung="BIC" wert={bic} setzeWert={setzeBic} />
          <Textfeld beschriftung="Bank" wert={bankName} setzeWert={setzeBankName} />
          <Betragsfeld beschriftung="Anfangssaldo" wert={saldo} setzeWert={setzeSaldo} />
          <Textfeld beschriftung="Saldo am" art="date" wert={saldoAm} setzeWert={setzeSaldoAm} />
        </div>
      </div>
    </Dialog>
  );
}

// =========================================================================
//  Umsatz von Hand erfassen
// =========================================================================

/**
 * Für Konten, für die es keinen Auszug gibt.
 *
 * Bargeld ist der Regelfall: Es gibt keine CAMT-Datei für die Kaffeekasse.
 * Ohne diese Maske hätte ein Barkonto keinen Weg, überhaupt Umsätze zu
 * bekommen.
 */
function UmsatzDialog({
  konten,
  schliessen,
  fertig,
}: {
  konten: Bankkonto[];
  schliessen: () => void;
  fertig: () => void;
}): ReactNode {
  const vorgang = useVorgang();
  const [bankkontoId, setzeBankkontoId] = useState(
    // Barkonten zuerst: Für sie ist diese Maske gedacht.
    konten.find((k) => k.art === 'bar')?.id ?? konten[0]?.id ?? '',
  );
  const [buchungsdatum, setzeBuchungsdatum] = useState(heute());
  const [richtung, setzeRichtung] = useState<'ausgabe' | 'einnahme'>('ausgabe');
  const [betrag, setzeBetrag] = useState('');
  const [gegenpartei, setzeGegenpartei] = useState('');
  const [zweck, setzeZweck] = useState('');

  async function speichern(): Promise<void> {
    // Das Vorzeichen ergibt sich aus der Richtung, nicht aus der Eingabe:
    // Ein Minus vor einem als „Ausgabe" gewählten Betrag würde ihn sonst
    // zur Einnahme machen.
    const vorzeichen = richtung === 'ausgabe' ? '-' : '';
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden('/api/bankumsaetze', {
        bankkontoId,
        buchungsdatum,
        betrag: `${vorzeichen}${betrag.trim().replace(/^[-+]/, '')}`,
        ...(gegenpartei.trim() ? { gegenparteiName: gegenpartei.trim() } : {}),
        ...(zweck.trim() ? { verwendungszweck: zweck.trim() } : {}),
      }),
    );
    if (ergebnis !== undefined) fertig();
  }

  return (
    <Dialog
      titel="Umsatz erfassen"
      schliessen={schliessen}
      fuss={
        <>
          <button type="button" className="knopf" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="button"
            className="knopf haupt"
            disabled={bankkontoId === '' || betrag.trim() === '' || vorgang.laeuft}
            onClick={() => void speichern()}
          >
            Erfassen
          </button>
        </>
      }
    >
      <div className="stapel">
        <Fehlermeldung text={vorgang.fehler} />
        <Meldung ton="info">
          Für Konten ohne Auszug — Bargeld etwa. Der Umsatz erscheint danach
          in der Liste und lässt sich einem Beleg zuordnen.
        </Meldung>
        <div className="formularraster">
          <div className="feld">
            <label htmlFor="umsatz-konto">
              Konto<span className="pflicht"> *</span>
            </label>
            <select
              id="umsatz-konto"
              value={bankkontoId}
              onChange={(e) => setzeBankkontoId(e.target.value)}
            >
              {konten.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.bezeichnung}
                </option>
              ))}
            </select>
          </div>
          <div className="feld">
            <label htmlFor="umsatz-richtung">Art</label>
            <select
              id="umsatz-richtung"
              value={richtung}
              onChange={(e) => setzeRichtung(e.target.value as 'ausgabe' | 'einnahme')}
            >
              <option value="ausgabe">Ausgabe (Geld geht weg)</option>
              <option value="einnahme">Einnahme (Geld kommt)</option>
            </select>
          </div>
          <Textfeld
            beschriftung="Buchungsdatum"
            art="date"
            wert={buchungsdatum}
            setzeWert={setzeBuchungsdatum}
            pflicht
          />
          <Betragsfeld beschriftung="Betrag" wert={betrag} setzeWert={setzeBetrag} pflicht />
          <Textfeld
            beschriftung="Gegenpartei"
            wert={gegenpartei}
            setzeWert={setzeGegenpartei}
            breit
            platzhalter="Bäckerei Müller"
          />
          <Textfeld
            beschriftung="Verwendungszweck"
            wert={zweck}
            setzeWert={setzeZweck}
            breit
          />
        </div>
      </div>
    </Dialog>
  );
}

// =========================================================================
//  Zuordnen
// =========================================================================

function ZuordnenDialog({
  umsatz,
  schliessen,
  fertig,
}: {
  umsatz: Bankumsatz;
  schliessen: () => void;
  fertig: () => void;
}): ReactNode {
  const vorschlaege = useAbruf<Zuordnungsvorschlag[]>(
    `/api/bankumsaetze/${umsatz.id}/vorschlaege`,
  );
  const vorgang = useVorgang();
  const [notiz, setzeNotiz] = useState('');

  async function ordneZu(belegId: string, konfidenz: number): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden(`/api/bankumsaetze/${umsatz.id}/zuordnen`, {
        belegId,
        art: 'vorschlag',
        konfidenz,
      }),
    );
    if (ergebnis !== undefined) fertig();
  }

  async function loese(zuordnungId: string): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.entfernen(`/api/zuordnungen/${zuordnungId}`),
    );
    if (ergebnis !== undefined) fertig();
  }

  async function kennzeichne(status: 'privat' | 'ignoriert'): Promise<void> {
    const ergebnis = await vorgang.fuehreAus(() =>
      api.senden(`/api/bankumsaetze/${umsatz.id}/kennzeichnen`, {
        status,
        ...(notiz.trim() ? { notiz: notiz.trim() } : {}),
      }),
    );
    if (ergebnis !== undefined) fertig();
  }

  return (
    <Dialog titel="Umsatz zuordnen" breit schliessen={schliessen}>
      <div className="stapel">
        <Fehlermeldung text={vorgang.fehler} />

        <div className="karte">
          <div className="karte-koerper">
            <dl className="paare">
              <dt>Datum</dt>
              <dd>{datum(umsatz.buchungsdatum)}</dd>
              <dt>Betrag</dt>
              <dd className="zahl">{euro(umsatz.betrag)}</dd>
              <dt>Gegenpartei</dt>
              <dd>{umsatz.gegenparteiName ?? '—'}</dd>
              <dt>Zweck</dt>
              <dd>{umsatz.verwendungszweck ?? '—'}</dd>
            </dl>
          </div>
        </div>

        {umsatz.zuordnungen.length > 0 ? (
          <div>
            <h3 style={{ marginBottom: '0.5rem' }}>Bereits zugeordnet</h3>
            <div className="dateiliste">
              {umsatz.zuordnungen.map((z) => (
                <div className="dateizeile" key={z.id}>
                  <span className="name">
                    <Link to={`/belege/${z.belegId}`}>{z.bezeichnung}</Link>
                    <span className="still klein"> {z.belegnummer ?? ''}</span>
                  </span>
                  <span className="zahl">{euro(z.betrag, false)}</span>
                  <button
                    type="button"
                    className="knopf klein gefahr"
                    disabled={vorgang.laeuft}
                    onClick={() => void loese(z.id)}
                  >
                    Lösen
                  </button>
                </div>
              ))}
            </div>
            <p className="klein still" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
              Lösen korrigiert die Verknüpfung, nicht die Buchung: Der Beleg
              behält Zahlungsdatum und Nummer. Soll die Zahlung selbst
              zurückgenommen werden, wird der Beleg storniert.
            </p>
          </div>
        ) : null}

        <div>
          <h3 style={{ marginBottom: '0.5rem' }}>Vorschläge</h3>
          {!vorschlaege.daten ? (
            <Laedt was="Offene Posten werden verglichen" />
          ) : vorschlaege.daten.length === 0 ? (
            <Leer
              titel="Kein passender offener Posten"
              hinweis="Vielleicht ist der Beleg noch nicht erfasst — oder der Umsatz ist privat."
            />
          ) : (
            <div className="tabelle-rahmen">
              <table className="tabelle">
                <thead>
                  <tr>
                    <th>Beleg</th>
                    <th className="zahl schmal">Betrag</th>
                    <th className="schmal">Sicherheit</th>
                    <th className="schmal" />
                  </tr>
                </thead>
                <tbody>
                  {vorschlaege.daten.map((v) => (
                    <tr key={v.belegId}>
                      <td>
                        <Link to={`/belege/${v.beleg.id}`} className="haupttext">
                          {v.beleg.bezeichnung}
                        </Link>
                        <div className="nebentext">
                          {v.beleg.rechnungsnummer ?? v.beleg.belegnummer ?? 'ohne Nummer'} ·{' '}
                          {datum(v.beleg.belegDatum)}
                          {v.beleg.kontaktName ? ` · ${v.beleg.kontaktName}` : ''}
                        </div>
                        <div className="nebentext still">{v.gruende.join(' · ')}</div>
                      </td>
                      <td className="zahl schmal">{euro(v.beleg.betragBrutto, false)}</td>
                      <td className="schmal">
                        {/* Ab 80 gilt ein Vorschlag als sicher genug für
                            einen Klick — siehe zuordnung-vorschlaege.ts. */}
                        <span
                          className={`marker ${v.konfidenz >= 80 ? 'gut' : v.konfidenz >= 50 ? 'warnung' : ''}`}
                        >
                          {v.konfidenz} %
                        </span>
                      </td>
                      <td className="schmal">
                        <button
                          type="button"
                          className="knopf klein haupt"
                          disabled={vorgang.laeuft}
                          onClick={() => void ordneZu(v.belegId, v.konfidenz)}
                        >
                          Zuordnen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <hr className="trenner" />

        <div className="stapel">
          <h3 style={{ margin: 0 }}>Oder aus der Arbeitsliste nehmen</h3>
          <p className="klein still" style={{ margin: 0 }}>
            Private Umsätze und Bagatellen gehören nicht in die EÜR. Gekennzeichnet
            bleiben sie erhalten, verschwinden aber aus der Liste der
            unerledigten.
          </p>
          <Textbereich
            beschriftung="Notiz"
            wert={notiz}
            setzeWert={setzeNotiz}
            zeilen={2}
            hinweis="Wird mit protokolliert"
          />
          <div className="reihe">
            <button
              type="button"
              className="knopf"
              disabled={vorgang.laeuft}
              onClick={() => void kennzeichne('privat')}
            >
              Als privat kennzeichnen
            </button>
            <button
              type="button"
              className="knopf"
              disabled={vorgang.laeuft}
              onClick={() => void kennzeichne('ignoriert')}
            >
              Als unerheblich kennzeichnen
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
